
import type React from "react"

import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { PlusIcon, MinusIcon, Loader2, Sparkles } from "lucide-react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AIExercisePlanGenerator } from "./ai-exercise-plan-generator"
import { useCreateExercisePlan, useGenerateExercisePlanWithAI } from "@/api/exercise-plans"
import { ScrollArea } from "@/components/ui/scroll-area"

interface AddExercisePlanDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddExercisePlanDialog({ open, onOpenChange }: AddExercisePlanDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const createExercisePlan = useCreateExercisePlan()
  const generateExercisePlanWithAI = useGenerateExercisePlanWithAI()
  const [activeTab, setActiveTab] = useState("manual")

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    difficulty: "beginner" as "beginner" | "intermediate" | "advanced",
    duration: "",
    targetMuscles: "",
    equipment: "",
    description: "",
  })

  const [exercises, setExercises] = useState([{ name: "", sets: 3, reps: "", rest: "", description: "" }])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  const handleSelectChange = (value: string) => {
    setFormData((prev) => ({ ...prev, difficulty: value as "beginner" | "intermediate" | "advanced" }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      console.log("Creating exercise plan with data:", {
        ...formData,
        duration: Number.parseInt(formData.duration),
        targetMuscles: formData.targetMuscles.split(",").map((item) => item.trim()),
        equipment: formData.equipment.split(",").map((item) => item.trim()),
        exercises: exercises.map((ex) => ({
          name: ex.name,
          sets: Number.parseInt(ex.sets.toString()),
          reps: ex.reps,
          rest: ex.rest,
          description: ex.description,
        })),
      })

      await createExercisePlan.mutateAsync({
        name: formData.name,
        type: formData.type,
        difficulty: formData.difficulty,
        duration: Number.parseInt(formData.duration),
        targetMuscles: formData.targetMuscles.split(",").map((item) => item.trim()),
        equipment: formData.equipment.split(",").map((item) => item.trim()),
        description: formData.description,
        exercises: exercises.map((ex) => ({
          name: ex.name,
          sets: Number.parseInt(ex.sets.toString()),
          reps: ex.reps,
          rest: ex.rest,
          description: ex.description,
        })),
      })

      // Reset form
      setFormData({
        name: "",
        type: "",
        difficulty: "beginner",
        duration: "",
        targetMuscles: "",
        equipment: "",
        description: "",
      })
      setExercises([{ name: "", sets: 3, reps: "", rest: "", description: "" }])

      // Close dialog
      onOpenChange(false)
    } catch (error) {
      console.error("Error creating exercise plan:", error)
      toast({
        title: t("common.error"),
        description: t("exercisePlans.errorCreatingPlan"),
        variant: "destructive",
      })
    }
  }

  const addExercise = () => {
    setExercises([...exercises, { name: "", sets: 3, reps: "", rest: "", description: "" }])
  }

  const removeExercise = (index: number) => {
    if (exercises.length > 1) {
      const newExercises = [...exercises]
      newExercises.splice(index, 1)
      setExercises(newExercises)
    }
  }

  const handleExerciseChange = (index: number, field: string, value: string | number) => {
    const updatedExercises = [...exercises]
    updatedExercises[index] = { ...updatedExercises[index], [field]: value }
    setExercises(updatedExercises)
  }

  const handleAIGenerate = async (prompt: string) => {
    try {
      const result = await generateExercisePlanWithAI.mutateAsync({ prompt })

      // Update form data with the AI generated plan
      setFormData({
        name: result.name,
        type: result.type,
        difficulty: result.difficulty,
        duration: result.duration.toString(),
        targetMuscles: result.targetMuscles.join(", "),
        equipment: result.equipment.join(", "),
        description: result.description,
      })

      setExercises(
        result.exercises.map((ex: any) => ({
          name: ex.name,
          sets: ex.sets,
          reps: ex.reps,
          rest: ex.rest,
          description: ex.description,
        })),
      )

      setActiveTab("manual")

      toast({
        title: t("ai.planGenerated"),
        description: t("ai.reviewAndEdit"),
      })
    } catch (error) {
      console.error("Error generating exercise plan with AI:", error)
      toast({
        title: t("common.error"),
        description: t("ai.generationFailed"),
        variant: "destructive",
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("exercisePlans.addPlan")}</DialogTitle>
          <DialogDescription>{t("exercisePlans.addPlanDescription")}</DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual">{t("common.manual")}</TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              {t("common.aiGenerate")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual">
            <form id="add-exercise-plan-form" onSubmit={handleSubmit}>
              <ScrollArea className="h-[500px] pr-4">
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      {t("exercisePlans.name")}
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="col-span-3"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="type" className="text-right">
                      {t("exercisePlans.type")}
                    </Label>
                    <Input
                      id="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="col-span-3"
                      placeholder="Strength, Cardio, Flexibility, etc."
                      required
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="difficulty" className="text-right">
                      {t("exercisePlans.difficulty")}
                    </Label>
                    <div className="col-span-3">
                      <Select value={formData.difficulty} onValueChange={handleSelectChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">{t("exercisePlans.beginner")}</SelectItem>
                          <SelectItem value="intermediate">{t("exercisePlans.intermediate")}</SelectItem>
                          <SelectItem value="advanced">{t("exercisePlans.advanced")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="duration" className="text-right">
                      {t("exercisePlans.duration")}
                    </Label>
                    <div className="col-span-3 flex items-center gap-2">
                      <Input
                        id="duration"
                        type="number"
                        min="1"
                        value={formData.duration}
                        onChange={handleInputChange}
                        className="flex-1"
                        required
                      />
                      <span>{t("exercisePlans.minutes")}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="targetMuscles" className="text-right">
                      {t("exercisePlans.targetMuscles")}
                    </Label>
                    <Input
                      id="targetMuscles"
                      value={formData.targetMuscles}
                      onChange={handleInputChange}
                      className="col-span-3"
                      placeholder="Chest, Back, Legs, etc. (comma separated)"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="equipment" className="text-right">
                      {t("exercisePlans.equipment")}
                    </Label>
                    <Input
                      id="equipment"
                      value={formData.equipment}
                      onChange={handleInputChange}
                      className="col-span-3"
                      placeholder="Dumbbells, Barbell, Bench, etc. (comma separated)"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-start gap-4">
                    <Label htmlFor="description" className="text-right pt-2">
                      {t("exercisePlans.description")}
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="col-span-3"
                      rows={3}
                      required
                    />
                  </div>

                  <div className="border-t pt-4 mt-2">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium">{t("exercisePlans.exercises")}</h3>
                      <Button type="button" variant="outline" size="sm" onClick={addExercise}>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {t("exercisePlans.addExercise")}
                      </Button>
                    </div>

                    {exercises.map((exercise, index) => (
                      <div key={index} className="border rounded-md p-4 mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium">
                            {t("exercisePlans.exercise")} {index + 1}
                          </h4>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeExercise(index)}
                            disabled={exercises.length <= 1}
                          >
                            <MinusIcon className="h-4 w-4" />
                            <span className="sr-only">{t("exercisePlans.removeExercise")}</span>
                          </Button>
                        </div>

                        <div className="grid gap-3">
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor={`exercise-${index}-name`} className="text-right">
                              {t("exercisePlans.exerciseName")}
                            </Label>
                            <Input
                              id={`exercise-${index}-name`}
                              value={exercise.name}
                              onChange={(e) => handleExerciseChange(index, "name", e.target.value)}
                              className="col-span-3"
                              required
                            />
                          </div>
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor={`exercise-${index}-sets`} className="text-right">
                              {t("exercisePlans.sets")}
                            </Label>
                            <Input
                              id={`exercise-${index}-sets`}
                              type="number"
                              min="1"
                              value={exercise.sets}
                              onChange={(e) => handleExerciseChange(index, "sets", Number.parseInt(e.target.value))}
                              className="col-span-3"
                              required
                            />
                          </div>
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor={`exercise-${index}-reps`} className="text-right">
                              {t("exercisePlans.reps")}
                            </Label>
                            <Input
                              id={`exercise-${index}-reps`}
                              value={exercise.reps}
                              onChange={(e) => handleExerciseChange(index, "reps", e.target.value)}
                              className="col-span-3"
                              placeholder="10, 8-12, etc."
                              required
                            />
                          </div>
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor={`exercise-${index}-rest`} className="text-right">
                              {t("exercisePlans.rest")}
                            </Label>
                            <Input
                              id={`exercise-${index}-rest`}
                              value={exercise.rest}
                              onChange={(e) => handleExerciseChange(index, "rest", e.target.value)}
                              className="col-span-3"
                              placeholder="60s, 90s, etc."
                              required
                            />
                          </div>
                          <div className="grid grid-cols-4 items-start gap-4">
                            <Label htmlFor={`exercise-${index}-description`} className="text-right pt-2">
                              {t("exercisePlans.exerciseDescription")}
                            </Label>
                            <Textarea
                              id={`exercise-${index}-description`}
                              value={exercise.description}
                              onChange={(e) => handleExerciseChange(index, "description", e.target.value)}
                              className="col-span-3"
                              rows={2}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollArea>
            </form>
          </TabsContent>

          <TabsContent value="ai">
            <div className="py-4">
              <AIExercisePlanGenerator
                title={t("ai.generateExercisePlan")}
                description={t("ai.exercisePlanDescription")}
                placeholder={t("ai.exercisePlanPlaceholder")}
                onGenerate={handleAIGenerate}
                isGenerating={generateExercisePlanWithAI.isPending}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="add-exercise-plan-form"
            disabled={createExercisePlan.isPending || activeTab === "ai"}
          >
            {createExercisePlan.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.creating")}
              </>
            ) : (
              t("common.create")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
